import React, { useEffect, useState, useContext, useRef, useCallback } from "react";
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import { Card } from 'primereact/card';
import { Skeleton } from 'primereact/skeleton';
import ContractContext from '../../ContractContext'; //Required for all dApp components using contracts

import { formatWalletAddress, formatCurrencyExtended, getTokenPrice } from '../../utils';

import ReactSelect from 'react-select';

import DashboardTokenInfo from "./DashboardTokenInfo";
import {
  infoStyle, cardHeaderStyle, infoOutlineStyle
} from "./TileStyles";
import BigNumber from 'bignumber.js'

import {
  LineChart, Line, BarChart, Bar, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, LabelList
} from 'recharts';

import { formatTokenLabel } from '../../utils';

import makeAnimated from 'react-select/animated';

const Dashboard = ({ data, getData, account, smartCodeData }) => {

  const isWalletConnected = () => account !== null;
  const contractContext = useContext(ContractContext);
  const contracts = contractContext.contracts;


  //states
  const [refuelUserData, setRefuelUserData] = useState({});
  const [refuelEarningsUSD, setRefuelEarningsUSD] = useState();
  const [earningsTrackerData, setEarningsTrackerData] = useState({});
  const [selectedTokens, setSelectedTokens] = useState([]);

  useEffect(() => {
    if (Object.keys(earningsTrackerData).length === 1) {
      const singleToken = Object.keys(earningsTrackerData)[0];
      const tokenData = data?.tokensList?.find(token => token?.address?.toLowerCase() === singleToken.toLowerCase());
      setSelectedTokens([{ value: tokenData?.address, label: tokenData?.label }]);
    }
  }, [earningsTrackerData, data?.tokensList]);

  //refs
  const toast = useRef(null);


  //styles
  const tokenCardStyle = {
    background: "#333333",
    borderRadius: "20px",
    color: "#FFFFFF",
    marginBottom: "10px",
    fontFamily: 'Inter, sans-serif',
    backgroundImage: `linear-gradient(263deg, rgb(255 148 49 / 85%), rgb(255 118 49/ 85%)),  linear-gradient(153deg,rgb(0, 0, 0), rgb(0, 0, 0) 19%, rgb(0, 0, 0))`,
    backgroundPosition: '0px 0px, 115% 50%, 0px 0px',
    backgroundSize: 'auto, auto 135%, auto',
    backgroundRepeat: 'repeat, repeat-y, repeat',
    backgroundAttachment: 'scroll, scroll, scroll',
    gridColumn: 'span 1',
    gridRow: 'span 1'
  };

  const tokenCardStyleAlt = {
    background: "#333333",
    borderRadius: "20px",
    color: "#FFFFFF",
    marginBottom: "10px",
    fontFamily: 'Inter, sans-serif',
    backgroundImage: `linear-gradient(263deg, rgb(202 90 90 / 85%), rgb(255 49 49 / 85%)),  linear-gradient(153deg,rgb(0, 0, 0), rgb(0, 0, 0) 19%, rgb(0, 0, 0))`,
    backgroundPosition: '0px 0px, 115% 50%, 0px 0px',
    backgroundSize: 'auto, auto 135%, auto',
    backgroundRepeat: 'repeat, repeat-y, repeat',
    backgroundAttachment: 'scroll, scroll, scroll',
    gridColumn: 'span 1',
    gridRow: 'span 1'
  };

  const rewardCardStyle = {
    background: "#333333",
    borderRadius: "20px",
    color: "#FFFFFF",
    marginBottom: "10px",
    fontFamily: 'Inter, sans-serif',
    backgroundImage: `linear-gradient(263deg, rgb(84 84 84 / 85%), rgb(45 45 45 / 85%)),  linear-gradient(153deg,rgb(0, 0, 0), rgb(0, 0, 0) 19%, rgb(0, 0, 0))`,
    backgroundPosition: '0px 0px, 115% 50%, 0px 0px',
    backgroundSize: 'auto, auto 135%, auto',
    backgroundRepeat: 'repeat, repeat-y, repeat',
    backgroundAttachment: 'scroll, scroll, scroll',
    gridColumn: 'span 1',
    gridRow: 'span 1'
  };


  const LoadingContent = () => <Skeleton width="50%" height="35px" className="mb-2" />;
  const LoadingTitle = () => <Skeleton width="25%" height="25px" className="mb-2" />;
  const LoadingChart = () => <Skeleton width="80%" height="150px" className="mb-2" />;

  //functions
  const getAllDepositSessions = useCallback(async () => {
    return await contracts.refuelDepositer.methods.getAllDepositSessions(account).call();
  }, [contracts.refuelDepositer, account])

  const getTotalDepositedByTokenSummary = async () => {
    return await contracts.refuelDepositer.methods.getTotalDepositedByTokenSummary(account).call();
  }

  const getTotalRewardsPending = async () => {
    return await contracts.refuelDepositer.methods.getTotalRewardsPending(account).call();
  }

  const getTotalRefuels = async () => {
    return await contracts.refuelDepositer.methods.totalRefuels().call();
  }

  const getRefuelUserData = useCallback(async () => {
    //console.log("Getting refuel user data for: ", account);
    return await contracts.refuelDepositer.methods.refuelUserData(account).call();
  }, [contracts.refuelDepositer, account])

  //gives a list of all tokens deposited
  const getDepositedTokens = useCallback(async () => {
    return await contracts.refuelDepositer.methods.getDepositedTokens().call();
  }, [contracts.refuelDepositer])

  const getRefuelEarnings = useCallback(async (token) => {
    return await contracts.refuelRewards.methods.shares(account, token).call();
  }, [contracts.refuelRewards])

  const getPendingRewards = useCallback(async (token) => {
    const result = await contracts.refuelRewards.methods.getTotalUnrealisedWithRealisedUndeposited(account, token).call();
    //console.log("getPendingRewards:", result);
    return result;
  }, [contracts.refuelRewards])


  const getEarningsTrackerData = useCallback(async (token) => {
    const index = await contracts.earningsTracker.methods.getIndexOfEarnings(contracts.refuelDepositer._address, account, token).call();
    // console.log("index:", index);
    if (index === 0 || data?.tokensList?.length === 0) {
      return [];
    }
    const rawData = [];
    for (let i = 0; i < index; i++) {
      const earningsData = await contracts.earningsTracker.methods.getEarnings(contracts.refuelDepositer._address, account, token, i).call();
      //  console.log("earningsTracker _data:", _data);
      const date = new Date(earningsData.timestamp * 1000).toLocaleDateString();
      const tokenSymbol = data?.tokensList?.find((tokenData) => tokenData.address.toLowerCase() === token.toLowerCase()).label;
      const tokenDecimals = data?.tokensList?.find((tokenData) => tokenData.address.toLowerCase() === token.toLowerCase()).decimals;
      const tokenPrice = getTokenPrice(data?.tokensList, tokenSymbol);

      rawData.push({
        ...earningsData,
        amount: parseFloat(earningsData.amount) / 10 ** tokenDecimals,
        amountUSD: parseFloat(earningsData.amount) / 10 ** tokenDecimals * tokenPrice,
        timestamp: parseFloat(earningsData.timestamp),
        totalCumulativeEarnings: parseFloat(data.totalCumulativeEarnings),
        date: date
      });
    }

    const uniqueDates = [...new Set(rawData.map(item => item.date))];

    const aggregatedData = uniqueDates.map(date => {
      const filteredData = rawData.filter(item => item.date === date);
      const sumAmount = filteredData.reduce((acc, curr) => acc + curr.amount, 0);
      const sumAmountUSD = filteredData.reduce((acc, curr) => acc + curr.amountUSD, 0);
      return {
        ...filteredData[0],  // take the first object to get other fields
        amount: sumAmount,  // replace the amount with the aggregated sum
        amountUSD: sumAmountUSD  // replace the amount with the aggregated sum
      };
    });

    return aggregatedData;
  }, [contracts.earningsTracker, contracts.refuelDepositer, account, data?.tokensList]);

  const calculateRefuelEarningsAllTokensUSD = useCallback(async () => {

    const depositedTokens = await getDepositedTokens();
    let totalEarningsUSD = 0;
    for (let i = 0; i < depositedTokens.length; i++) {
      const token = depositedTokens[i];
      const _earnings = await getRefuelEarnings(token);
      const tokenSymbol = data.tokensList.find((tokenData) => tokenData.address.toLowerCase() === token.toLowerCase()).label;
      const tokenDecimals = data.tokensList.find((tokenData) => tokenData.address.toLowerCase() === token.toLowerCase()).decimals;
      const tokenPrice = getTokenPrice(data.tokensList, tokenSymbol);
      const _earningsUSD = new BigNumber(_earnings.totalRealised).div(10 ** tokenDecimals).multipliedBy(tokenPrice).toNumber();
      const _earningsPendingUSD = new BigNumber(await getPendingRewards(token)).div(10 ** tokenDecimals).multipliedBy(tokenPrice).toNumber();
      totalEarningsUSD += _earningsUSD + _earningsPendingUSD;
    }
    setRefuelEarningsUSD(totalEarningsUSD);

  }, [getDepositedTokens, getRefuelEarnings, getPendingRewards, data?.tokensList]);


  const refreshBalances = async () => {
    calculateRefuelEarningsAllTokensUSD();
  }


  // Assuming you have a variable or state that determines if dark mode is active
  const isDarkMode = true; // Set this to your actual dark mode state

  const darkModeColors = {
    text: '#EAEAEA',          // Light gray for dark backgrounds
    axis: '#888888',         // Some gray color for axis
    background: '#333333',   // Dark background
    bar: '#8884d8',          // Your bar color, you can adjust as needed
    tooltip: '#444444'       // Tooltip background
  };

  const lightModeColors = {
    text: '#333333',
    axis: '#888888',
    background: '#FFFFFF',
    bar: '#8884d8',
    tooltip: '#FFFFFF'
  };

  const colors = isDarkMode ? darkModeColors : lightModeColors;

  const dateTickFormatter = (timestamp) => {
    const dateObject = new Date(timestamp);
    const formattedDate = `${dateObject.getMonth() + 1}/${dateObject.getDate()}/${dateObject.getFullYear()}`;
    return formattedDate;
  }



  //useEffects
  useEffect(() => {
    if (isWalletConnected() && contracts.refuelDepositer) {
      getRefuelUserData().then((data) => {
        setRefuelUserData(data);
        //console.log("Refuel user data: ", data);
      });
    }
  }, [account, contracts]);

  useEffect(() => {
    if (isWalletConnected() && contracts.refuelRewards && data.tokensList) {
      calculateRefuelEarningsAllTokensUSD();
    }
  }, [account, contracts, data]);

  useEffect(() => {
    const fetchAllEarningsData = async () => {
      const depositedTokens = await getDepositedTokens();
      let _allEarnings = {};
      //for loop to build array of pending reward tokens to be used for the "Claim" button generated for each token
      for (let i = 0; i < depositedTokens.length; i++) {
        const token = depositedTokens[i];
        const _earnings = await getEarningsTrackerData(token);
        if (_earnings.length > 0) {
          _allEarnings[token] = _earnings;
        }
      }
      setEarningsTrackerData(_allEarnings);

    }

    if (data && contracts.refuelDepositer && contracts.earningsTracker) {
      fetchAllEarningsData();
    }
  }, [data, contracts.refuelDepositer, contracts.earningsTracker]);

  useEffect(() => {
    //console.log("earningsTrackerData:", earningsTrackerData);
  }, [earningsTrackerData]);


  const gridContainerStyle = {
    display: 'grid',
    gap: '10px',
    gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
    marginTop: '10px',
  };

  return (
    <div style={gridContainerStyle}>


        <div>
          <Card title={
            <div style={cardHeaderStyle}>
              {<>
                <Button outlined disabled rounded
                  style={{
                    paddingLeft: '6px', paddingRight: '6px', paddingTop: '2px', paddingBottom: '2px', marginBottom: '4px', color: 'white', size: 'lg',
                    backgroundColor: 'black', fontSize: '14px', fontWeight: 'bold'
                  }}>
                  {
                    smartCodeData?.smartCode !== undefined && smartCodeData?.smartCode !== ''
                      ? smartCodeData?.smartCode : formatWalletAddress(account)
                  }</Button>
                <br />
              </>}
              Refuel Rewards Earned</div>
          }
            style={
              tokenCardStyle
            }>
            <div style={infoStyle}>
              {refuelEarningsUSD !== undefined && refuelEarningsUSD !== null
                ? formatCurrencyExtended(refuelEarningsUSD)
                : <Skeleton width="100%" height="35px" className="mb-2" />}
            </div>
          </Card>

          <Card title={
            <div style={cardHeaderStyle}>
              Refuels Attended</div>
          }
            style={
              tokenCardStyleAlt
            }>
            <div style={infoStyle}>
              {refuelUserData?.refuelsAttended
                ? refuelUserData.refuelsAttended
                : <Skeleton width="100%" height="35px" className="mb-2" />}
            </div>
          </Card>
        </div>
        {/* </div>


      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(min(300px, 100%), 1fr))',
        gap: '10px',
      }}> */}


        <DashboardTokenInfo data={data} getData={getData} refreshBalances={refreshBalances} />

      { //verify earningsTrackerData is not empty object
        Object.keys(earningsTrackerData).length > 0 && data?.tokensList &&
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(min(300px, 100%), 1fr))',
          gap: '10px',
        }}>
          <Card title={
            <div style={infoOutlineStyle}>Rewards Earned</div>
          }
            style={
              rewardCardStyle
            }>
            <ReactSelect
              id="token-selection"
              options={
                Object.keys(earningsTrackerData).map((token) => {
                  const tokenData = data.tokensList.find((tokenData) => tokenData.address.toLowerCase() === token.toLowerCase());
                  return {
                    value: tokenData.address,
                    label: tokenData.label,
                  };
                }).sort((a, b) => a.label.localeCompare(b.label))
              }
              isMulti={Object.keys(earningsTrackerData).length > 1}
              isDisabled={Object.keys(earningsTrackerData).length === 1}
              value={selectedTokens}
              onChange={(tokens) => setSelectedTokens(tokens)}
              components={makeAnimated()}
              placeholder="Select a Token"
              formatOptionLabel={(option) => formatTokenLabel(data?.dbData, { value: option.value, label: option.label })}
              //we want border radius to be 10px

              styles={{
                container: (provided, state) => ({
                  ...provided,
                  //background: "#333333",
                  border: "none",
                  borderRadius: "20px",
                  boxShadow: "none",
                  color: "white",
                  fontFamily: "Inter, sans-serif",
                  fontSize: "16px",
                  fontWeight: "500",
                  minHeight: "48px",
                  //padding: "0.5rem",
                  "&:hover": {
                    borderColor: "#333333",
                  },
                }),
                control: (provided, state) => ({
                  ...provided,
                  //background: "#333333",
                  border: "none",
                  borderRadius: "40px",
                  boxShadow: "none",
                  color: "white",
                  fontFamily: "Inter, sans-serif",
                  fontSize: "16px",
                  fontWeight: "500",
                  minHeight: "48px",
                  padding: "0.5rem",
                  //marginBottom: "0",
                  //paddingBottom: "0",
                  "&:hover": {
                    borderColor: "#333333",
                  },
                }),
                menu: (provided, state) => ({
                  ...provided,
                  background: "white",
                  border: "none",
                  borderBottomLeftRadius: "20px",
                  borderBottomRightRadius: "20px",
                  boxShadow: "none",
                  color: "#333333",
                  fontFamily: "Inter, sans-serif",
                  fontSize: "16px",
                  fontWeight: "500",
                  padding: "0.5rem",
                  marginTop: "0px",
                }),
                // option: (provided, state) => ({
                //   ...provided,
                //   background: state.isFocused ? "#333333" : "#333333",
                //   color: state.isFocused ? "white" : "white",
                //   fontFamily: "Inter, sans-serif",
                //   fontSize: "16px",
                //   fontWeight: "500",
                //   padding: "0.5rem",
                // }),
                multiValue: (provided, state) => ({
                  ...provided,
                  background: "#333333",
                  color: "white",
                  fontFamily: "Inter, sans-serif",
                  fontSize: "16px",
                  fontWeight: "500",
                  padding: "0.15rem",
                  borderRadius: "20px",
                }),
                multiValueLabel: (provided, state) => ({
                  ...provided,
                  color: "white",
                  fontFamily: "Inter, sans-serif",
                  fontSize: "16px",
                  fontWeight: "700",
                }),
                multiValueRemove: (provided, state) => ({
                  ...provided,
                  color: "white",
                  fontFamily: "Inter, sans-serif",
                  fontSize: "16px",
                  fontWeight: "500",
                  borderRadius: "20px",
                  "&:hover": {
                    color: "white",
                    cursor: "pointer",
                  },
                }),
                indicatorSeparator: (provided, state) => ({
                  ...provided,
                  background: "#333333",
                }),
                dropdownIndicator: (provided, state) => ({
                  ...provided,
                  color: "#333333",
                  "&:hover": {
                    color: "#333333",
                  },
                }),
                clearIndicator: (provided, state) => ({
                  ...provided,
                  color: "#333333",
                  "&:hover": {
                    color: "#333333",
                  },
                }),
                placeholder: (provided, state) => ({
                  ...provided,
                  color: "#333333",
                }),
                input: (provided, state) => ({
                  ...provided,
                  color: "#333333",
                }),

              }}
            />
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(min(200px, 100%), 1fr))',
              gap: '10px',
              marginTop: '10px'
            }}>
              {selectedTokens.map(token => (
                <ResponsiveContainer key={token.value} width="100%" height={200}>
                  <BarChart
                    data={earningsTrackerData[token.value]}
                    margin={{
                      top: 25, right: 5, left: -15, bottom: 5,
                    }}
                    background={colors.background}
                  >
                    <XAxis
                      dataKey="date"
                      name="Date"
                      tickFormatter={dateTickFormatter}
                      stroke={colors.text}
                      axisLine={{ stroke: colors.axis }}
                      tickLine={{ stroke: colors.axis }}
                      tick={{ fontSize: 12 }}
                    />
                    <YAxis
                      dataKey="amountUSD"
                      name="Rewards"
                      tickFormatter={(value) => formatCurrencyExtended(value)}
                      stroke={colors.text}
                      axisLine={{ stroke: colors.axis }}
                      tickLine={{ stroke: colors.axis }}
                      tick={{ fontSize: 12 }}
                    />
                    <Tooltip
                      formatter={(value) => formatCurrencyExtended(value)}
                      wrapperStyle={{ background: colors.tooltip }}
                      contentStyle={{ borderColor: colors.text, backgroundColor: '#000000' }}
                      labelStyle={{ color: colors.text }}
                      itemStyle={{ color: colors.text }}
                    />
                    <Legend
                      formatter={(value) => value}
                      wrapperStyle={{ color: colors.text }}
                    />
                    <Bar
                      type="monotone"
                      dataKey="amountUSD"
                      name={token.label + " Rewards Earned"}
                      fill={colors.bar}
                    >
                      <LabelList dataKey="amountUSD" position="top" formatter={formatCurrencyExtended} style={{ fill: colors.text }} />
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              ))}
            </div>
          </Card> </div>
      }
      <Toast ref={toast} />
    </div>
  );
};

export default React.memo(Dashboard);;

